import React, {useCallback, useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {Paper, useTheme} from '@mui/material';
import {resolveFloatingPanelLayout} from './floatingPanelPosition';

export default function FloatingPanel({
                                          open,
                                          anchorEl,
                                          anchorPosition,
                                          onClose,
                                          width = 320,
                                          id,
                                          elevation = 6,
                                          preventMouseDownDefault = true,
                                          sx,
                                          children,
                                      }) {
    const panelRef = useRef(null);
    const [layout, setLayout] = useState({visible: false, left: 0, top: 0});
    const theme = useTheme();

    const updatePosition = useCallback(() => {
        const panelWidth = panelRef.current?.offsetWidth || width;
        const panelHeight = panelRef.current?.offsetHeight || 0;
        setLayout(resolveFloatingPanelLayout({
            anchorEl,
            anchorPosition,
            panelWidth,
            panelHeight,
        }));
    }, [anchorEl, anchorPosition, width]);

    useEffect(() => {
        if (!open) return undefined;

        updatePosition();
        const raf = requestAnimationFrame(updatePosition);
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);

        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [open, updatePosition]);

    useEffect(() => {
        if (!open || layout.visible) return undefined;
        const raf = requestAnimationFrame(updatePosition);
        return () => cancelAnimationFrame(raf);
    }, [open, layout.visible, updatePosition]);

    useEffect(() => {
        if (!open) return undefined;

        const onPointerDown = (event) => {
            const target = event.target;
            if (panelRef.current?.contains(target)) return;
            if (anchorEl?.contains?.(target)) return;
            onClose?.(event, 'backdropClick');
        };

        document.addEventListener('pointerdown', onPointerDown, true);
        return () => document.removeEventListener('pointerdown', onPointerDown, true);
    }, [open, onClose, anchorEl]);

    useEffect(() => {
        if (!open) return undefined;

        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose?.(event, 'escapeKeyDown');
            }
        };

        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [open, onClose]);

    if (!open || (!anchorEl && !anchorPosition)) {
        return null;
    }

    return createPortal(
        <Paper
            ref={panelRef}
            id={id}
            elevation={elevation}
            onMouseDown={(event) => {
                if (preventMouseDownDefault) {
                    event.preventDefault();
                }
            }}
            sx={{
                display: 'block',
                position: 'fixed',
                left: layout.left,
                top: layout.top,
                visibility: layout.visible ? 'visible' : 'hidden',
                zIndex: (theme) => theme.zIndex.modal + 1,
                pointerEvents: layout.visible ? 'auto' : 'none',
                width,
                maxWidth: '90vw',
                border: '1px solid',
                borderColor: theme.palette.divider,
                ...sx,
            }}
        >
            {children}
        </Paper>,
        document.body
    );
}
